import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, HelpCircle, Loader2, Search, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { API } from "@/lib/api";
import HeaderV4 from "@/components/header-v4/HeaderV4";
import FooterV4 from "@/components/footer-v4/FooterV4";


function fetchFaq(lang) {
    const url = `${API}/faq?lang=${encodeURIComponent(lang || "en")}`;
    return fetch(url, { credentials: "omit" })
        .then((r) => {
            if (!r.ok) throw new Error(`FAQ fetch failed (${r.status})`);
            return r.json();
        });
}


export default function PublicFAQ() {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "en").split("-")[0];
    const [data, setData] = useState({ items: [], count: 0, lang });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [query, setQuery] = useState("");
    const [openIdx, setOpenIdx] = useState(0);

    useEffect(() => {
        setLoading(true);
        fetchFaq(lang)
            .then((d) => { setData(d || { items: [], count: 0 }); setErr(null); })
            .catch((e) => setErr(e.message))
            .finally(() => setLoading(false));
    }, [lang]);

    // Client-side search filter
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return data.items;
        return (data.items || []).filter(
            (it) => (it.question || "").toLowerCase().includes(q)
                 || (it.answer || "").toLowerCase().includes(q),
        );
    }, [data.items, query]);

    // Group by category
    const grouped = useMemo(() => {
        const g = new Map();
        for (const it of filtered) {
            const c = it.category || "general";
            if (!g.has(c)) g.set(c, []);
            g.get(c).push(it);
        }
        return Array.from(g.entries());
    }, [filtered]);

    // Sync <title> + <meta description> client-side too (SSR sets initial values)
    useEffect(() => {
        document.title = `Frequently Asked Questions · Global Lead Suite`;
    }, []);

    return (
        <div data-testid="faq-page" className="min-h-screen bg-black text-text-main">
            {/* Hotfix 2026-10 · unified public chrome · HeaderV4 + FooterV4
                replace the FAQ-only header/footer so brand parity is kept
                with the SSR SiteHeader/SiteFooter rendered on all other
                marketing pages. */}
            <HeaderV4 />

            <main className="max-w-3xl mx-auto px-6 py-16 md:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-3">
                        <HelpCircle className="h-3 w-3 text-[var(--color-secondary)]" />
                        Support · Phase 17
                    </div>
                    <h1
                        data-testid="faq-heading"
                        className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.1]"
                    >
                        Frequently Asked Questions
                    </h1>
                    <p className="text-sm text-text-secondary mt-3 max-w-xl">
                        Everything you need to know about Global Lead Suite —
                        {" "}<span className="text-text-main tabular-nums">{data.count}</span> answers, curated.
                    </p>
                </motion.div>

                <div className="mt-8 relative">
                    <Search className="h-4 w-4 text-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        data-testid="faq-search"
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search questions…"
                        className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] pl-10 pr-4 py-3 text-sm text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-[var(--color-primary)]"
                    />
                </div>

                <div className="mt-8 space-y-6">
                    {loading && (
                        <div className="flex items-center justify-center py-16 text-text-secondary text-xs gap-2">
                            <Loader2 className="h-3 w-3 animate-spin" /> Loading FAQs…
                        </div>
                    )}
                    {err && !loading && (
                        <div
                            data-testid="faq-error"
                            className="rounded-2xl border border-[var(--color-alert)]/30 bg-[var(--color-alert)]/5 p-6 text-sm text-[var(--color-alert)]"
                        >
                            {err}
                        </div>
                    )}
                    {!loading && !err && filtered.length === 0 && (
                        <div
                            data-testid="faq-empty"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-10 text-center text-sm text-text-secondary"
                        >
                            {query
                                ? `No results for "${query}". Try a different keyword.`
                                : "No FAQs published yet. Check back soon."}
                        </div>
                    )}

                    {!loading && !err && grouped.map(([cat, items]) => (
                        <section key={cat} data-testid={`faq-cat-${cat}`}>
                            <h2 className="text-[10px] uppercase tracking-widest text-text-secondary mb-3">
                                {cat.replace(/_/g, " ")}
                            </h2>
                            <div className="space-y-2">
                                {items.map((it) => (
                                    <FaqItem
                                        key={it.id || it.order}
                                        item={it}
                                        open={openIdx === it.order}
                                        onToggle={() => setOpenIdx(openIdx === it.order ? -1 : it.order)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-16 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 text-sm">
                    <p className="text-text-main font-medium">Still have questions?</p>
                    <p className="text-xs text-text-secondary mt-1">
                        Ping us on{" "}
                        <a href="/app/subscription" className="text-[var(--color-secondary)] hover:underline">
                            the pricing page
                        </a>{" "}
                        or reach out via support and we'll add your question here.
                    </p>
                </div>
            </main>

            <FooterV4 />
        </div>
    );
}


function FaqItem({ item, open, onToggle }) {
    return (
        <div
            data-testid={`faq-item-${item.id || item.order}`}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
        >
            <button
                onClick={onToggle}
                data-testid={`faq-toggle-${item.id || item.order}`}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
            >
                <span className="text-sm text-text-main font-medium leading-snug">
                    {item.question}
                </span>
                <ChevronDown
                    className={`h-4 w-4 text-text-secondary shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            <motion.div
                initial={false}
                animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
            >
                <div className="px-5 pb-5 pt-0 text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                    {item.answer}
                </div>
            </motion.div>
        </div>
    );
}
